import React, { useMemo } from "react";
import Card from "@mui/material/Card";
import { CardHeader } from "@mui/material";
import PlaidPayrollIncomeTable, { formatDate } from "../Plaid/PlaidPayrollIncomeTable";
import { NumericFormat } from "react-number-format";
import {
  calculatePlaidCoveredLookbackWeeks,
} from "../../../utils/PlaidPayrollGrossPay";
import ShowDocumentStatus from "../components/ShowDocumentStatus";
import ReviewPendingTile from "../components/ReviewPendingTile";
import { PayTile } from "../../Molecules/PayTile";
import overa from "../../../assets/images/overa.svg";
import greenTick from "../../../assets/images/greenTick.svg";
import { isDocumentReviewed, shouldShowReviewPending } from "../../../utils/screeningScore";
import { toIncomeSourceTitleCase } from "../../../utils/incomeSourceDisplay";
import ArgylePayrollValidationMessages from "./ArgylePayrollValidationMessages";

type PlaidEmployerSourceProps = {
  employer: any;
  displaySourceNumber: number;
  role?: string;
  payrollPending?: boolean;
  requiredLookbackWeeks?: number | null;
  trn_id?: any;
  applicationStatusUpdatedOn?: string | null;
  validationMessages?: Record<string, any[]>;
};

const PlaidEmployerSource = ({
  employer,
  displaySourceNumber,
  role,
  payrollPending = false,
  requiredLookbackWeeks = null,
  trn_id,
  applicationStatusUpdatedOn,
  validationMessages,
}: PlaidEmployerSourceProps) => {
  const showDetails = role !== "applicant";

  const plaidMonthlyGross = employer?.avgMonthlyGross ?? 0;
  const plaidMonthlyNet = employer?.avgMonthlyNet ?? 0;
  const plaidCoveredWeeks = useMemo(
    () => calculatePlaidCoveredLookbackWeeks(employer?.payrollStatements),
    [employer?.payrollStatements]
  );
  const normalizedRequiredLookback = Number(requiredLookbackWeeks) || 0;
  const providerType = employer?.payroll_provider === "argyle" || employer?.argyle_account_id
    ? "embedded"
    : employer?.plaid_item_id
      ? "plaid"
      : null;
  const providerLabel = providerType === "embedded" ? "Payroll Link" : providerType === "plaid" ? "Plaid" : null;
  const isArgylePayroll = providerType === "embedded";
  const employerName = isArgylePayroll
    ? toIncomeSourceTitleCase(employer?.employer_name) || "-"
    : employer?.employer_name || "-";

  const noStatements =
    Array.isArray(employer?.payrollStatements) && employer.payrollStatements.length === 0;
  const lookbackSatisfied =
    normalizedRequiredLookback <= 0 || plaidCoveredWeeks >= normalizedRequiredLookback;
  const lookbackText = `${plaidCoveredWeeks} (required ${normalizedRequiredLookback})`;
  const plaidStatusValue = noStatements || payrollPending ? 3 : 1;
  const plaidStatusLabel = noStatements ? undefined : payrollPending ? "Pending" : "Valid";

  // Gate on the link table timestamp (COALESCE(ModifiedOn, CreatedOn)) so a
  // source linked after the last Review Done stays pending until re-reviewed.
  // Fails closed: missing timestamp → subscriber sees Review Pending.
  const sourceUpdatedOn = employer?.source_updated_on ?? null;
  const isReviewed = sourceUpdatedOn != null
    ? isDocumentReviewed(sourceUpdatedOn, applicationStatusUpdatedOn)
    : false;
  const reviewPending = shouldShowReviewPending(isReviewed, role);

  return (
    <div className="pt-1">
      <div className="login-radio-types p-2">
        <div className="row pb-2">
          <div className="col-12">
            <Card
              sx={{
                background: "#f5fdff",
                boxShadow: "none",
                borderBottom: "1px solid black",
                borderRadius: 0,
              }}
            >
              <CardHeader
                sx={{
                  padding: 0,
                  "& .MuiCardHeader-action": {
                    margin: 0,
                    alignSelf: "center",
                    paddingRight: "6px",
                  },
                }}
                title={
                  <b style={{ fontSize: 13 }}>{`Employer ${displaySourceNumber}: ${employerName}`}</b>
                }
                action={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {providerLabel ? (
                      <span
                        data-testid="payroll-provider-badge"
                        data-provider={providerLabel.toLowerCase()}
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#fff",
                          background:
                            providerType === "embedded" ? "#0a7a4d" : "#1d4ed8",
                          lineHeight: 1.4,
                          whiteSpace: "nowrap",
                        }}
                        aria-label={`Linked via ${providerLabel}`}
                      >
                        {providerLabel}
                      </span>
                    ) : null}
                    <small
                      style={{
                        color: "#6c757d",
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        textAlign: "right",
                      }}
                    >
                      Connected payroll employers cannot be deleted
                    </small>
                  </div>
                }
              />
            </Card>
          </div>
        </div>

        {showDetails && reviewPending && (
          <div className="pr-2 pt-2">
            <ReviewPendingTile className={"col-lg-3 col-sm-3 col-3"} />
          </div>
        )}

        {showDetails && !reviewPending && (
          <div className="appl-status-wrap mb-2">
            <div className="d-flex flex-wrap">
              <div className="pr-2 pt-2">
                <ShowDocumentStatus
                  label={"Overall"}
                  trn_id={null}
                  documentId={3}
                  SourceId={displaySourceNumber}
                  value={plaidStatusValue}
                  src={null}
                  blockStyle={""}
                  invalidateQueries={"income"}
                  className={"col-lg-3 col-sm-3 col-3"}
                  textStyle={""}
                  disable={true}
                  customWidth={150}
                  statusLabelOverride={noStatements ? undefined : plaidStatusLabel}
                  statusLabelStyle={noStatements ? { fontFamily: "'OpenSans-SemiBold.ttf'" } : undefined}
                />
              </div>
              <div className="px-2 pt-2">
                <div className="block">
                  <div
                    style={{
                      fontSize: "12px",
                      lineHeight: "16px",
                      textAlign: "center",
                    }}
                  >
                    No. of Weeks
                  </div>
                  <img src={lookbackSatisfied ? greenTick : overa} alt="" />
                  <span
                    style={{
                      fontSize: "12px",
                      lineHeight: "16px",
                      textAlign: "center",
                    }}
                  >
                    {lookbackText}
                  </span>
                </div>
              </div>
              {employer?.employment_start_date && (
                <div className="px-2 pt-2">
                  <div className="block" data-testid="employment-start-date-tile">
                    <div
                      style={{
                        fontSize: "12px",
                        lineHeight: "16px",
                        textAlign: "center",
                      }}
                    >
                      Employment Start Date
                    </div>
                    <br />
                    <span
                      style={{
                        fontSize: "12px",
                        lineHeight: "16px",
                        textAlign: "center",
                        paddingTop: "4px",
                      }}
                    >
                      {formatDate(employer.employment_start_date)}
                    </span>
                  </div>
                </div>
              )}
              <div className="px-2 pt-2">
                <PayTile
                  lookBackPeriodSatisfied={lookbackSatisfied}
                  title="Monthly Gross Pay"
                  payAmt={plaidMonthlyGross}
                />
              </div>
              <div className="px-2 pt-2">
                <PayTile
                  lookBackPeriodSatisfied={lookbackSatisfied}
                  title="Monthly Net Pay"
                  payAmt={plaidMonthlyNet}
                />
              </div>
            </div>
          </div>
        )}

        {!showDetails && (
          <div className="d-flex justify-content-end mb-2 print-hide">
            <b className="fw-normal sub-text">
              Monthly Gross Pay:{" "}
              <NumericFormat
                thousandSeparator={","}
                prefix={"$"}
                decimalScale={2}
                displayType={"text"}
                value={plaidMonthlyGross}
              />
            </b>
          </div>
        )}

        {isArgylePayroll && (
          <ArgylePayrollValidationMessages
            role={role}
            validationMessages={validationMessages}
            sourceId={displaySourceNumber}
            trnId={trn_id}
          />
        )}

        {showDetails && (
          <div className="row align-items-center mb-2 print-hide">
            <div className="col-12 col-md-4 col-lg-4">
              <label className="lable-title">Employer Name</label>
              <div className="cus-control d-flex align-items-center px-2">
                {employerName}
              </div>
            </div>
            <div className="col-12 col-md-8 col-lg-8">
              <label className="lable-title">Address</label>
              <div className="cus-control d-flex align-items-center px-2">
                {employer?.employer_address}
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-4">
              <label className="lable-title">City</label>
              <div className="cus-control d-flex align-items-center px-2">
                {employer?.employer_city}
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-4">
              <label className="lable-title">State</label>
              <div className="cus-control d-flex align-items-center px-2">
                {employer?.employer_state}
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-4">
              <label className="lable-title">Zip Code</label>
              <div className="cus-control d-flex align-items-center px-2">
                {employer?.employer_zipcode}
              </div>
            </div>
          </div>
        )}

        <div className="mt-2">
          {noStatements ? (
            <p style={{color: '#666', margin: '8px 0', fontStyle: 'italic'}}>
              No recent income found. Link another employer or upload recent pay stubs or bank statements.
            </p>
          ) : (
            <PlaidPayrollIncomeTable payrollStatements={employer?.payrollStatements} />
          )}
        </div>

      </div>
    </div>
  );
};

export default PlaidEmployerSource;
